import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Descriptions,
  Input,
  List,
  Modal,
  Radio,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  CloudDownloadOutlined,
  PlusOutlined,
  SearchOutlined,
  VideoCameraOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import {
  directorsApi,
  genresApi,
  movieDatasourceApi,
  type DatasourceCandidate,
  type MovieInfoResponse,
} from "../services/api";
import { usePermissions } from "../hooks/usePermissions";

interface AIMovieHelperProps {
  onAccept: (movieInfo: MovieInfoResponse, addedDirectorId?: string) => void;
  onAddMissingDirector?: (directorName: string) => void;
  onDirectorAdded?: (callback: (directorId: string) => void) => void;
}

const isPosterLikeImage = (kind?: string | null) => {
  const normalized = (kind || "poster").toLowerCase();
  return normalized === "poster" || normalized === "cover";
};

const isAdditionalPhotoImage = (kind?: string | null) => {
  const normalized = (kind || "").toLowerCase();
  return normalized === "photo" || normalized === "still" || normalized === "backdrop";
};

const imageKindLabel = (kind?: string | null) => {
  const normalized = (kind || "").toLowerCase();
  if (normalized === "backdrop") return "zdjecie z filmu";
  if (normalized === "photo" || normalized === "still") return "zdjecie";
  if (normalized === "poster" || normalized === "cover") return "plakat";
  return normalized || "obraz";
};

export function AIMovieHelper({
  onAccept,
  onAddMissingDirector,
  onDirectorAdded,
}: AIMovieHelperProps) {
  const [searchTitle, setSearchTitle] = useState("");
  const [candidates, setCandidates] = useState<DatasourceCandidate[]>([]);
  const [movieInfo, setMovieInfo] = useState<MovieInfoResponse | null>(null);
  const [selectedCandidate, setSelectedCandidate] = useState<DatasourceCandidate | null>(null);
  const [isCandidateModalOpen, setIsCandidateModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isFallbackFetching, setIsFallbackFetching] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [currentProgress, setCurrentProgress] = useState("");
  const [progressMessages, setProgressMessages] = useState<string[]>([]);
  const [addedDirectorId, setAddedDirectorId] = useState<string | undefined>();
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);
  const [selectedAdditionalImageUrls, setSelectedAdditionalImageUrls] = useState<string[]>([]);
  const { canCreate } = usePermissions();

  const { data: genres } = useQuery({
    queryKey: ["genres-all"],
    queryFn: () => genresApi.list({ page_size: 100 }),
  });

  const { data: directors } = useQuery({
    queryKey: ["directors-all"],
    queryFn: () => directorsApi.list({ page_size: 200 }),
  });

  useEffect(() => {
    if (onDirectorAdded) {
      onDirectorAdded((directorId: string) => setAddedDirectorId(directorId));
    }
  }, [onDirectorAdded]);

  const isWorking = isFetching || isFallbackFetching;

  useEffect(() => {
    if (!isWorking) {
      setElapsedSeconds(0);
      return;
    }

    const startedAt = Date.now();
    setElapsedSeconds(0);
    const intervalId = window.setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [isWorking]);

  const imageOptions = useMemo(() => {
    const fromCandidates = movieInfo?.image_candidates || [];
    if (fromCandidates.length > 0) return fromCandidates;
    if (movieInfo?.cover_url) {
      return [
        {
          image_id: "cover_url",
          source: "datasource",
          url: movieInfo.cover_url,
          thumbnail_url: movieInfo.cover_url,
          label: "Cover",
          kind: "cover",
        },
      ];
    }
    return [];
  }, [movieInfo]);

  const posterOptions = useMemo(
    () => imageOptions.filter((image) => isPosterLikeImage(image.kind)),
    [imageOptions],
  );

  const coverOptions = posterOptions.length > 0 ? posterOptions : imageOptions;

  const additionalPhotoOptions = useMemo(
    () => imageOptions.filter((image) => isAdditionalPhotoImage(image.kind)),
    [imageOptions],
  );

  const imageMeta = (image: (typeof imageOptions)[number]) => {
    const parts = [
      imageKindLabel(image.kind),
      image.language ? `jezyk: ${image.language}` : null,
      image.width && image.height ? `${image.width}x${image.height}` : null,
      typeof image.score === "number" ? `ocena: ${image.score.toFixed(1)}` : null,
    ].filter(Boolean);
    return parts.join(" | ");
  };

  const matchGenresAndDirector = (data: MovieInfoResponse) => {
    const matchedGenreIds: string[] = [];
    let matchedDirectorId: string | undefined;

    if (data.suggested_genres && genres?.items) {
      data.suggested_genres.forEach((genreName) => {
        const lower = genreName.toLowerCase();
        const match = genres.items.find((g) => {
          const name = g.name.toLowerCase();
          return name === lower || name.includes(lower) || lower.includes(name);
        });
        if (match) matchedGenreIds.push(match.id);
      });
    }

    if (data.suggested_directors?.length && directors?.items) {
      const directorName = data.suggested_directors[0];
      const lower = directorName.toLowerCase();
      const match = directors.items.find((d) => {
        const name = d.name.toLowerCase();
        return name === lower || name.includes(lower) || lower.includes(name);
      });
      if (match) matchedDirectorId = match.id;
    }

    return { ...data, genre_ids: matchedGenreIds, director_id: matchedDirectorId };
  };

  const formatProgressMessage = (msg: string): string => {
    try {
      const parsed = JSON.parse(msg);
      return parsed.message || parsed.source || msg;
    } catch {
      return msg.replace(/^["']|["']$/g, "");
    }
  };

  const handleSearch = async () => {
    const title = searchTitle.trim();
    if (!title) {
      message.warning("Wprowadz tytul filmu");
      return;
    }

    setIsFetching(true);
    setProgressMessages([]);
    setCurrentProgress("Szukanie kandydatow...");

    try {
      const result = await movieDatasourceApi.fetchMovieCandidates({ title });
      const items = result.items || [];
      if (items.length === 0) {
        message.warning("Nie znaleziono kandydatow");
        return;
      }
      setCandidates(items);
      setIsCandidateModalOpen(true);
    } catch (error: unknown) {
      message.error(error instanceof Error && error.message ? error.message : "Nie udalo sie wyszukac kandydatow");
    } finally {
      setIsFetching(false);
      setCurrentProgress("");
    }
  };

  const handleCandidateSelect = async (candidate: DatasourceCandidate, useWebFallback = false) => {
    if (!useWebFallback) {
      setIsCandidateModalOpen(false);
      setSelectedCandidate(candidate);
      setIsFetching(true);
    } else {
      setIsFallbackFetching(true);
    }
    setProgressMessages([]);
    setCurrentProgress(useWebFallback ? "Szukanie dodatkowych danych w sieci..." : "Pobieranie szczegolow...");

    try {
      const data = await movieDatasourceApi.fetchMovieDetailsStreaming(
        { title: searchTitle.trim(), candidate, use_web_fallback: useWebFallback },
        (progressMsg) => {
          const formatted = formatProgressMessage(progressMsg);
          setCurrentProgress(formatted);
          setProgressMessages((prev) => [...prev, formatted]);
        },
      );
      const enriched = matchGenresAndDirector(data);
      const preferredCoverUrl = enriched.image_candidates?.find((image) => isPosterLikeImage(image.kind))?.url;
      const defaultImage =
        selectedImageUrl && enriched.image_candidates?.some((image) => image.url === selectedImageUrl)
          ? selectedImageUrl
          : preferredCoverUrl || enriched.cover_url || enriched.image_candidates?.[0]?.url || null;
      setSelectedImageUrl(defaultImage);
      setMovieInfo(enriched);
      if (!useWebFallback) {
        setSelectedAdditionalImageUrls([]);
      } else {
        setSelectedAdditionalImageUrls((prev) =>
          prev.filter((url) => enriched.image_candidates?.some((image) => image.url === url)),
        );
      }
      if (!useWebFallback) {
        setAddedDirectorId(undefined);
      }
      setIsDetailsModalOpen(true);
    } catch (error: unknown) {
      message.error(error instanceof Error && error.message ? error.message : "Nie udalo sie pobrac szczegolow");
    } finally {
      setIsFetching(false);
      setIsFallbackFetching(false);
      setCurrentProgress("");
      setProgressMessages([]);
    }
  };

  const handleAccept = () => {
    if (!movieInfo) return;
    onAccept(
      {
        ...movieInfo,
        cover_url: selectedImageUrl || movieInfo.cover_url,
        additional_image_urls: selectedAdditionalImageUrls.filter((url) => url !== selectedImageUrl),
      },
      addedDirectorId,
    );
    setIsDetailsModalOpen(false);
    setSearchTitle("");
    setCandidates([]);
    setMovieInfo(null);
    setSelectedCandidate(null);
    setSelectedImageUrl(null);
    setSelectedAdditionalImageUrls([]);
    setAddedDirectorId(undefined);
    message.success("Dane zostaly wypelnione w formularzu");
  };

  const suggestedDirector = movieInfo?.suggested_directors?.[0];
  const hasDirectorInDatabase = movieInfo?.director_id !== undefined;
  const wasDirectorAddedInSession = addedDirectorId !== undefined;
  const showDirectorAsMatched = hasDirectorInDatabase || wasDirectorAddedInSession;

  return (
    <>
      <style>{`
        .ai-movie-details-modal {
          top: 16px;
          max-width: calc(100vw - 32px);
          padding-bottom: 0;
        }

        .ai-movie-details-modal .ant-modal-content {
          max-height: calc(100vh - 32px);
          display: flex;
          flex-direction: column;
        }

        .ai-movie-details-modal .ant-modal-body {
          flex: 1 1 auto;
          min-height: 0;
          overflow-y: auto;
        }
      `}</style>
      <Card
        title={
          <Space>
            <VideoCameraOutlined />
            <span>Asystent danych - Pobierz informacje o filmie</span>
          </Space>
        }
        style={{ marginBottom: 24 }}
        size="small"
      >
        <Space.Compact style={{ width: "100%" }}>
          <Input
            placeholder="Wprowadz tytul filmu, np. Zawodowcy"
            value={searchTitle}
            onChange={(e) => setSearchTitle(e.target.value)}
            onPressEnter={handleSearch}
            disabled={isFetching}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch} loading={isFetching}>
            Szukaj
          </Button>
        </Space.Compact>

        {isWorking && (
          <div style={{ marginTop: 16, textAlign: "center" }}>
            <Spin />
            <Space style={{ marginTop: 8 }}>
              <span>{currentProgress || "Pracuje..."}</span>
              <Tag color="processing">Czas: {elapsedSeconds} s</Tag>
            </Space>
            {progressMessages.length > 0 && (
              <div style={{ marginTop: 8, fontSize: 12, color: "#595959" }}>
                {progressMessages.slice(-4).map((msg, idx) => (
                  <div key={`${msg}-${idx}`}>{msg}</div>
                ))}
              </div>
            )}
          </div>
        )}

        <Alert
          title="Zrodlo danych"
          description="Najpierw zobaczysz liste kandydatow. Po wyborze filmu pobierzemy szczegoly i plakaty."
          type="info"
          showIcon
          style={{ marginTop: 12 }}
        />
      </Card>

      <Modal
        title="Wybierz film"
        open={isCandidateModalOpen}
        onCancel={() => setIsCandidateModalOpen(false)}
        footer={null}
        width={900}
        styles={{ body: { maxHeight: "calc(100vh - 220px)", overflowY: "auto" } }}
      >
        <List
          itemLayout="horizontal"
          dataSource={candidates}
          renderItem={(candidate) => (
            <List.Item
              actions={[
                <Button key="select" type="primary" onClick={() => handleCandidateSelect(candidate)}>
                  Wybierz
                </Button>,
              ]}
            >
              <List.Item.Meta
                avatar={
                  candidate.thumbnail_url ? (
                    <img src={candidate.thumbnail_url} alt="" style={{ width: 56, height: 84, objectFit: "cover" }} />
                  ) : null
                }
                title={
                  <Space wrap>
                    <Typography.Text strong>{candidate.title || "Bez tytulu"}</Typography.Text>
                    {candidate.year && <Tag>{candidate.year}</Tag>}
                    <Tag color="blue">{candidate.source}</Tag>
                  </Space>
                }
                description={
                  <Space direction="vertical" size={2}>
                    {candidate.original_title && candidate.original_title !== candidate.title && (
                      <Typography.Text type="secondary">{candidate.original_title}</Typography.Text>
                    )}
                    {candidate.description && (
                      <Typography.Paragraph ellipsis={{ rows: 2 }} style={{ marginBottom: 0 }}>
                        {candidate.description}
                      </Typography.Paragraph>
                    )}
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      </Modal>

      <Modal
        title={
          <Space>
            <VideoCameraOutlined />
            <span>Dane pobrane z zewnetrznych zrodel</span>
          </Space>
        }
        open={isDetailsModalOpen}
        onOk={handleAccept}
        onCancel={() => {
          setIsDetailsModalOpen(false);
          setMovieInfo(null);
          setSelectedImageUrl(null);
        }}
        width={1200}
        className="ai-movie-details-modal"
        style={{ top: 8, maxWidth: "calc(100vw - 32px)" }}
        styles={{
          header: { flex: "0 0 auto" },
          body: {
            overflow: "hidden",
            padding: "16px 24px",
          },
          footer: { flex: "0 0 auto" },
        }}
        okText="Akceptuj i wypelnij formularz"
        cancelText="Anuluj"
        okButtonProps={{ icon: <CheckOutlined /> }}
        cancelButtonProps={{ icon: <CloseOutlined /> }}
      >
        <div style={{ maxHeight: "calc(100vh - 170px)", overflowY: "auto", paddingRight: 12 }}>
          {movieInfo && (
            <>
            {suggestedDirector && !showDirectorAsMatched && (
              <Alert
                title="Brakujacy rezyser w slowniku"
                description="Rezyser nie zostal znaleziony w bazie. Mozesz go dodac ponizej."
                type="warning"
                icon={<WarningOutlined />}
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="Tytul">{movieInfo.title || <i>Brak danych</i>}</Descriptions.Item>
              <Descriptions.Item label="Tytul oryginalny">
                {movieInfo.original_title || <i>Brak danych</i>}
              </Descriptions.Item>
              <Descriptions.Item label="Rok premiery">
                {movieInfo.release_year || <i>Brak danych</i>}
              </Descriptions.Item>
              <Descriptions.Item label="Opis">{movieInfo.description || <i>Brak danych</i>}</Descriptions.Item>
              <Descriptions.Item label="Czas trwania">
                {movieInfo.duration_minutes ? `${movieInfo.duration_minutes} min` : <i>Brak danych</i>}
              </Descriptions.Item>
              <Descriptions.Item label="Jezyk oryginalny">
                {movieInfo.original_language || movieInfo.original_language_code || <i>Brak danych</i>}
              </Descriptions.Item>
              <Descriptions.Item label="Kraj produkcji">
                {movieInfo.production_countries?.length ? movieInfo.production_countries.join(", ") : <i>Brak danych</i>}
              </Descriptions.Item>
              <Descriptions.Item label="Rezyser">
                {suggestedDirector ? (
                  <Space wrap>
                    <Tag color={showDirectorAsMatched ? "blue" : "default"}>{suggestedDirector}</Tag>
                    {showDirectorAsMatched && (
                      <Tag color="green">{wasDirectorAddedInSession ? "Dodano w tej sesji" : "Znaleziono w bazie"}</Tag>
                    )}
                    {!showDirectorAsMatched && onAddMissingDirector && (
                      <Button
                        size="small"
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => onAddMissingDirector(suggestedDirector)}
                        disabled={!canCreate}
                      >
                        Dodaj do slownika
                      </Button>
                    )}
                  </Space>
                ) : (
                  <i>Brak danych</i>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Gatunki">
                {movieInfo.suggested_genres?.length ? (
                  <Space wrap>{movieInfo.suggested_genres.map((genre) => <Tag key={genre}>{genre}</Tag>)}</Space>
                ) : (
                  <i>Brak danych</i>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Plakat / okladka">
                <div style={{ marginBottom: 8 }}>
                  <Button
                    icon={<CloudDownloadOutlined />}
                    loading={isFallbackFetching}
                    disabled={!selectedCandidate || isFallbackFetching}
                    onClick={() => selectedCandidate && handleCandidateSelect(selectedCandidate, true)}
                  >
                    Szukaj dodatkowo w sieci
                  </Button>
                  {isFallbackFetching && (
                    <Space style={{ marginLeft: 12 }}>
                      <Spin size="small" />
                      <Typography.Text type="secondary">
                        {currentProgress || "Szukanie dodatkowych danych..."}
                      </Typography.Text>
                      <Tag color="processing">Czas: {elapsedSeconds} s</Tag>
                    </Space>
                  )}
                </div>
                {coverOptions.length ? (
                  <Radio.Group
                    value={selectedImageUrl}
                    onChange={(e) => setSelectedImageUrl(e.target.value)}
                    style={{ width: "100%" }}
                  >
                    <List
                      size="small"
                      bordered
                      dataSource={coverOptions}
                      renderItem={(image) => {
                        const selected = selectedImageUrl === image.url;
                        return (
                          <List.Item
                            style={{
                              alignItems: "center",
                              background: selected ? "#e6f4ff" : undefined,
                              cursor: "pointer",
                            }}
                            onClick={() => setSelectedImageUrl(image.url)}
                          >
                            <Space align="center" style={{ width: "100%" }}>
                              <Radio value={image.url} />
                              <img
                                src={image.thumbnail_url || image.url}
                                alt={image.label || "Plakat"}
                                style={{
                                  width: 54,
                                  height: 78,
                                  objectFit: "contain",
                                  border: "1px solid #d9d9d9",
                                  background: "#fff",
                                }}
                              />
                              <Space direction="vertical" size={2} style={{ minWidth: 0 }}>
                                <Space wrap size={4}>
                                  <Tag color={image.source === "tmdb" ? "blue" : image.source === "omdb" ? "green" : "default"}>
                                    {image.source}
                                  </Tag>
                                  {image.language && <Tag color={image.language === "pl" ? "red" : "default"}>{image.language}</Tag>}
                                  {selected && <Tag color="processing">Wybrany</Tag>}
                                </Space>
                                <Typography.Text strong ellipsis>
                                  {image.label || "Plakat"}
                                </Typography.Text>
                                <Typography.Text type="secondary" ellipsis>
                                  {imageMeta(image)}
                                </Typography.Text>
                                <Typography.Link href={image.url} target="_blank" rel="noreferrer">
                                  Otworz obraz
                                </Typography.Link>
                                <Checkbox
                                  checked={selectedAdditionalImageUrls.includes(image.url)}
                                  disabled={image.url === selectedImageUrl}
                                  onClick={(event) => event.stopPropagation()}
                                  onChange={(event) => {
                                    const checked = event.target.checked;
                                    setSelectedAdditionalImageUrls((prev) =>
                                      checked
                                        ? Array.from(new Set([...prev, image.url]))
                                        : prev.filter((url) => url !== image.url),
                                    );
                                  }}
                                >
                                  Dodaj jako dodatkowy obrazek
                                </Checkbox>
                              </Space>
                            </Space>
                          </List.Item>
                        );
                      }}
                    />
                  </Radio.Group>
                ) : (
                  <i>Brak danych</i>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Dodatkowe zdjecia">
                {additionalPhotoOptions.length ? (
                  <List
                    size="small"
                    bordered
                    dataSource={additionalPhotoOptions}
                    renderItem={(image) => {
                      const checked = selectedAdditionalImageUrls.includes(image.url);
                      return (
                        <List.Item
                          style={{
                            alignItems: "center",
                            background: checked ? "#f6ffed" : undefined,
                          }}
                        >
                          <Space align="center" style={{ width: "100%" }}>
                            <Checkbox
                              checked={checked}
                              onChange={(event) => {
                                const isChecked = event.target.checked;
                                setSelectedAdditionalImageUrls((prev) =>
                                  isChecked
                                    ? Array.from(new Set([...prev, image.url]))
                                    : prev.filter((url) => url !== image.url),
                                );
                              }}
                            />
                            <img
                              src={image.thumbnail_url || image.url}
                              alt={image.label || "Zdjecie"}
                              style={{
                                width: 96,
                                height: 54,
                                objectFit: "cover",
                                border: "1px solid #d9d9d9",
                                background: "#fff",
                              }}
                            />
                            <Space direction="vertical" size={2} style={{ minWidth: 0 }}>
                              <Space wrap size={4}>
                                <Tag color={image.source === "filmweb" ? "red" : image.source === "tmdb" ? "blue" : "default"}>
                                  {image.source}
                                </Tag>
                                <Tag>{imageKindLabel(image.kind)}</Tag>
                              </Space>
                              <Typography.Text strong ellipsis>
                                {image.source === "tmdb" && image.kind === "backdrop"
                                  ? "TMDB zdjecie z filmu"
                                  : image.label || "Zdjecie"}
                              </Typography.Text>
                              <Typography.Text type="secondary" ellipsis>
                                {imageMeta(image)}
                              </Typography.Text>
                              <Typography.Link href={image.url} target="_blank" rel="noreferrer">
                                Otworz obraz
                              </Typography.Link>
                            </Space>
                          </Space>
                        </List.Item>
                      );
                    }}
                  />
                ) : (
                  <i>Brak danych</i>
                )}
              </Descriptions.Item>
            </Descriptions>
            </>
          )}
        </div>
      </Modal>
    </>
  );
}
